import React from 'react';
import { twMerge } from 'tailwind-merge';
import { BaseButton } from '@repo/common';

import { ReactComponent as BackpackOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_backpack_outline.svg';
import { ReactComponent as BusinessBriefcaseOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_business-briefcase_version1_outline.svg';
import { ReactComponent as GraduateOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_graduate_outline.svg';
import { ReactComponent as LanguageProgramOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_language-program_outline.svg';
import { ReactComponent as ManualBookOutlineIcon } from '@repo/common/assets/icons/24px/icon_24px_book_outline.svg';
import { ReactComponent as ReloadIcon } from '@repo/common/assets/icons/24px/icon_24px_reload.svg';

const DEFAULT_LABELS = {
  title: 'Option',
  caption: 'caption',
};

const PATHFINDER_OPTION_ICONS = {
  'Get a Job in Korea': <BusinessBriefcaseOutlineIcon />,
  'Graduate School (Master/PhD)': <GraduateOutlineIcon />,
  'Undergraduate Degree (BA)': <ManualBookOutlineIcon />,
  'Transfer to a Korean University': <ReloadIcon />,
  'Language Program': <LanguageProgramOutlineIcon />,
};

// 전달된 아이콘을 32px 박스 안 24px 글리프로 정규화 (currentColor 상속)
const renderIcon = (node) =>
  React.isValidElement(node)
    ? React.cloneElement(node, {
        'aria-hidden': node.props['aria-hidden'] ?? true,
        className: twMerge('size-6', node.props.className),
      })
    : node;

/**
 * PathfinderMobileSelect — Pathfinder 온보딩(모바일) 선택 옵션 재사용 컴포넌트
 *
 * Figma: Pathfinder Onboarding - mobile (node 2:1485)
 * 데스크톱 대응 컴포넌트는 `option/PathfinderSelectBar` 이며 prop API를 동일하게 맞췄다.
 *
 * @component
 * @param {string} title 메인 텍스트 (chip은 이 값만 사용)
 * @param {string} caption 보조 텍스트 (chip/none 미사용 가능)
 * @param {"small" | "large" | "chip"} size 카드(small/large) 또는 pill(chip)
 * @param {"default" | "selected" | "saved"} state 선택 상태
 * @param {"left" | "right" | "center" | "none"} iconPosition 아이콘 배치 (large는 center)
 * @param {React.ReactNode} icon 기본 상태 아이콘
 * @param {React.ReactNode} [selectedIcon] selected/saved 상태 아이콘 (미지정 시 icon 재사용)
 * @param {string} className 외부에서 덮어쓸 Tailwind className
 * @param {("button" | "submit" | "reset")} type 버튼 type
 *
 * @example
 * <PathfinderMobileSelect
 *   title="Get a Job in Korea"
 *   caption="Start your career"
 *   size="small"
 *   iconPosition="left"
 *   state="selected"
 *   onClick={handleSelect}
 * />
 */
function PathfinderMobileSelect({
  title = DEFAULT_LABELS.title,
  caption = DEFAULT_LABELS.caption,
  size = 'small',
  state = 'default',
  iconPosition = 'left',
  icon,
  selectedIcon,
  textAlign = 'auto',
  className = '',
  type = 'button',
  ...props
}) {
  const isChip = size === 'chip';
  const isTall = size === 'tall';
  const isLarge = size === 'large' || isTall;
  const isActive = state === 'selected' || state === 'saved';
  const defaultIcon = PATHFINDER_OPTION_ICONS[title] ?? <BackpackOutlineIcon />;
  const displayIcon = icon === undefined ? defaultIcon : icon;
  const showIcon = !isChip && iconPosition !== 'none' && Boolean(displayIcon);
  const resolvedTextAlign = textAlign === 'auto' && !showIcon ? 'left' : textAlign;
  const shouldCenterContent =
    resolvedTextAlign !== 'left' && (isLarge || iconPosition === 'none');

  // ─── chip: 텍스트만 있는 pill ───────────────────────────────────────────────
  if (isChip) {
    return (
      <BaseButton
        type='text'
        size='m'
        htmlType={type}
        hideIcon
        typo={false}
        isStatic
        textWrapperClass={twMerge(
          'h-auto flex-none whitespace-nowrap typo-b14Strong [word-break:break-word]',
          isActive ? 'text-text-success' : 'text-text-tertiary'
        )}
        className={twMerge(
          'relative h-auto min-w-0 cursor-pointer items-center justify-center rounded-[100px] border-[1.6px] border-solid px-[14px] py-[9px] transition-colors duration-200',
          state === 'selected'
            ? 'border-surface-green40 bg-green-light01'
            : 'border-transparent bg-grayscale-gray01',
          className
        )}
        {...props}
      >
        {title}
      </BaseButton>
    );
  }

  const iconElement = showIcon ? (
    <span
      className={twMerge(
        'flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-[8px] transition-colors duration-200',
        // 아이콘 배경은 large(center)에서만 노출
        isLarge && (state === 'selected' ? 'bg-surface-green10' : 'bg-grayscale-gray02/50'),
        isActive ? 'text-icon-success' : 'text-icon-placeholder'
      )}
    >
      {renderIcon(isActive ? selectedIcon ?? displayIcon : displayIcon)}
    </span>
  ) : null;

  const contentWrapperClass = twMerge(
    'flex h-auto min-w-0 flex-col gap-[2px] [word-break:break-word]',
    shouldCenterContent
      ? 'w-full flex-none items-center text-center'
      : 'flex-1 items-start text-left'
  );

  const content = (
    <>
      <span
        className={twMerge(
          'w-full typo-b14Strong',
          isActive ? 'text-text-success' : 'text-text-tertiary'
        )}
      >
        {title}
      </span>
      {caption ? (
        <span
          className={twMerge(
            'w-full typo-b12',
            isActive ? 'text-text-success' : 'text-text-placeholder'
          )}
        >
          {caption}
        </span>
      ) : null}
    </>
  );

  const showIconAfter = showIcon && !isLarge && iconPosition === 'right';

  // ─── small / large 카드 ─────────────────────────────────────────────────────
  return (
    <BaseButton
      type='text'
      size='m'
      htmlType={type}
      typo={false}
      isStatic
      icon={iconElement}
      iconPosition={showIconAfter ? 'after' : 'before'}
      gap={isLarge || showIcon ? 'gap-2' : 'gap-0'}
      textWrapperClass={contentWrapperClass}
      className={twMerge(
        /*
         * whitespace-normal 이 필요하다 — BaseButton 루트에 whitespace-nowrap 이 고정으로
         * 붙어 있어서, 2열 그리드 안의 카드에서 긴 문구("Yes! Show me recommendations")가
         * 한 줄로 밀려 잘린다. nowrap 이 이기기 때문에 안쪽 word-break 도 먹지 않는다.
         */
        'relative h-auto w-full min-w-0 cursor-pointer items-center whitespace-normal break-words rounded-[12px] border-[1.6px] border-solid p-0 transition-colors duration-200',
        isTall
          ? 'h-[240px] flex-col justify-center gap-2 p-4'
          : isLarge
          ? 'flex-col justify-center gap-2 p-4'
          : 'justify-center px-4 py-[14px]',
        !isLarge && showIcon ? 'gap-2' : '',
        isLarge ? '[&>span]:flex-col [&>span]:justify-center' : '',
        state === 'selected'
          ? 'border-surface-green40 bg-green-light01'
          : 'border-transparent bg-surface-lightgray60',
        className
      )}
      {...props}
    >
      {content}
    </BaseButton>
  );
}

export default PathfinderMobileSelect;
